import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cloud, KeyRound, AlertCircle, CheckCircle2, Trash2, Eye, EyeOff, RefreshCw, X, Database } from "lucide-react";
import { Card, CardBody, CardHeader } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { api } from "../lib/api";

interface ServiceSummary {
  serviceId: string;
  displayName: string;
  serviceType: "s3";
  awsAccessKeyId: string;
  region: string;
  bucketName?: string;
  hasSecret?: boolean;
  updatedAt: string;
}

export default function Services() {
  const [items, setItems] = useState<ServiceSummary[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ServiceSummary | null>(null);

  // Form states
  const [displayName, setDisplayName] = useState("");
  const [serviceType, setServiceType] = useState<"s3">("s3");
  const [awsAccessKeyId, setAwsAccessKeyId] = useState("");
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState("");
  const [region, setRegion] = useState("us-east-1");
  const [bucketName, setBucketName] = useState("");

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testing, setTesting] = useState<string | null>(null); // serviceId being tested
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = () => {
    api.getRaw<any>("/api/services")
      .then((res) => {
        const data = res && res.data ? res.data : (Array.isArray(res) ? res : []);
        setItems(data);
      })
      .catch(() => setItems([]));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreateModal = () => {
    setEditingItem(null);
    setDisplayName("");
    setServiceType("s3");
    setAwsAccessKeyId("");
    setAwsSecretAccessKey("");
    setRegion("us-east-1");
    setBucketName("");
    setTestResult(null);
    setShowSecret(false);
    setError(null);
    setModalOpen(true);
  };

  const openEditModal = (item: ServiceSummary) => {
    setEditingItem(item);
    setDisplayName(item.displayName);
    setServiceType(item.serviceType);
    setAwsAccessKeyId(item.awsAccessKeyId);
    setAwsSecretAccessKey(""); // Don't prefill password
    setRegion(item.region);
    setBucketName(item.bucketName ?? "");
    setTestResult(null);
    setShowSecret(false);
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || !awsAccessKeyId.trim() || !region.trim()) {
      setError("Please fill out all required fields.");
      return;
    }
    if (!editingItem && !awsSecretAccessKey.trim()) {
      setError("AWS Secret Access Key is required for new connections.");
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      displayName: displayName.trim(),
      serviceType,
      awsAccessKeyId: awsAccessKeyId.trim(),
      region: region.trim(),
      bucketName: bucketName.trim() || undefined,
      ...(awsSecretAccessKey.trim() ? { awsSecretAccessKey: awsSecretAccessKey.trim() } : {}),
    };

    try {
      if (editingItem) {
        await api.patch(`/api/services/${editingItem.serviceId}`, payload);
      } else {
        await api.post("/api/services", payload);
      }
      closeModal();
      fetchItems();
    } catch (err: any) {
      setError(err.message ?? "Failed to save connection.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingItem) return;
    if (!window.confirm(`Are you sure you want to delete "${editingItem.displayName}"?`)) return;

    setDeleting(true);
    setError(null);
    try {
      await api.del(`/api/services/${editingItem.serviceId}`);
      closeModal();
      fetchItems();
    } catch (err: any) {
      setError(err.message ?? "Failed to delete connection.");
    } finally {
      setDeleting(false);
    }
  };

  const handleTestConnection = async (serviceId: string) => {
    setTesting(serviceId);
    setTestResult(null);
    try {
      const res = await api.post<{ ok: boolean; message: string }>(`/api/services/${serviceId}/test`, {});
      setTestResult(res);
    } catch (err: any) {
      setTestResult({
        ok: false,
        message: err.message ?? "Connection test failed.",
      });
    } finally {
      setTesting(null);
    }
  };

  if (items === null) return <div className="text-sm text-gray-500">Loading…</div>;
 
  const listItems = Array.isArray(items) ? items : [];
 
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Services</h1>
          <p className="mt-1 text-sm text-gray-500">
            Connect external storage and cloud services. Linked services automate file retrieval
            (e.g., fetching private Amazon S3 attachments in drafts) before sending.
          </p>
        </div>
        <Button onClick={openCreateModal}>+ Connect Service</Button>
      </div>
 
      {listItems.length === 0 ? (
        <Card>
          <CardBody>
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-gray-100">
                <Cloud className="h-6 w-6 text-gray-500" />
              </div>
              <p className="mt-4 text-base font-semibold text-gray-900">No external services connected</p>
              <p className="mt-1 max-w-md text-sm text-gray-500">
                Connect your Amazon S3 bucket to automatically download files linked in drafts
                and attach them as actual email attachments.
              </p>
              <div className="mt-4 flex gap-2">
                <Button size="sm" onClick={openCreateModal}>
                  Connect Amazon S3
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {listItems.map((s) => (
            <Card key={s.serviceId} className="flex flex-col justify-between hover:border-gray-300 transition-colors">
              <CardBody className="space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-brand-100 text-brand-700">
                      <Database className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{s.displayName}</h3>
                      <span className="inline-flex items-center rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-600/20">
                        Amazon S3
                      </span>
                    </div>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => openEditModal(s)}>
                    Edit
                  </Button>
                </div>

                <div className="space-y-2 border-t border-gray-100 pt-3 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Access Key:</span>
                    <span className="font-mono text-gray-900 font-semibold">{s.awsAccessKeyId.slice(0, 8)}...</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Region:</span>
                    <span className="font-mono text-gray-900">{s.region}</span>
                  </div>
                  {s.bucketName && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Bucket Name:</span>
                      <span className="font-mono text-gray-900 truncate max-w-[150px]">{s.bucketName}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Secret Set:</span>
                    <span>
                      {s.hasSecret ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                          <CheckCircle2 className="h-3 w-3" /> Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                          <AlertCircle className="h-3 w-3" /> No
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Connection testing row */}
                <div className="border-t border-gray-100 pt-3 flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500">Integration Connection</span>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={testing === s.serviceId}
                      onClick={() => handleTestConnection(s.serviceId)}
                    >
                      {testing === s.serviceId ? (
                        <span className="flex items-center gap-1">
                          <RefreshCw className="h-3 w-3 animate-spin" /> Testing...
                        </span>
                      ) : (
                        "Test connection"
                      )}
                    </Button>
                  </div>

                  {testing === s.serviceId && (
                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded border border-gray-100">
                      Contacting AWS S3 endpoint...
                    </div>
                  )}

                  {testResult && !testing && listItems.find(i => i.serviceId === s.serviceId) && (
                    <div className={`text-xs p-2 rounded border ${
                      testResult.ok 
                        ? "bg-emerald-50 text-emerald-800 border-emerald-200" 
                        : "bg-rose-50 text-rose-800 border-rose-200"
                    }`}>
                      <p className="font-semibold">{testResult.ok ? "Success!" : "Connection Failed:"}</p>
                      <p className="mt-0.5 font-mono break-all">{testResult.message}</p>
                      </div>
                  )}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity" onClick={closeModal}></div>
 
            {/* Modal Box */}
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <form onSubmit={handleSave}>
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="flex items-start justify-between border-b border-gray-100 pb-3">
                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                      {editingItem ? `Edit Service: ${editingItem.displayName}` : "Connect Service"}
                    </h3>
                    <button type="button" className="text-gray-400 hover:text-gray-500 cursor-pointer" onClick={closeModal}>
                      <X className="h-5 w-5" />
                    </button>
                  </div>
 
                  <div className="mt-4 space-y-4">
                    <Input
                      label="Connection Name"
                      placeholder="e.g. Production S3 bucket"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      required
                    />
 
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">Service Type</label>
                      <select
                        value={serviceType}
                        onChange={(e) => setServiceType(e.target.value as "s3")}
                        className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                      >
                        <option value="s3">Amazon S3</option>
                        <option value="gcs" disabled>Google Cloud Storage (Coming Soon)</option>
                        <option value="azure" disabled>Azure Blob Storage (Coming Soon)</option>
                      </select>
                    </div>
 
                    {serviceType === "s3" && (
                      <>
                        <Input
                          label="AWS Access Key ID"
                          placeholder="e.g. AKIAIOSFODNN7EXAMPLE"
                          value={awsAccessKeyId}
                          onChange={(e) => setAwsAccessKeyId(e.target.value)}
                          required
                        />
 
                        <div className="space-y-1.5">
                          <div className="flex justify-between items-center">
                            <label className="block text-sm font-medium text-gray-900">
                              AWS Secret Access Key
                            </label>
                            <button
                              type="button"
                              onClick={() => setShowSecret(!showSecret)}
                              className="text-xs text-gray-500 hover:text-gray-900 cursor-pointer flex items-center gap-1"
                            >
                              {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                              {showSecret ? "Hide" : "Show"}
                            </button>
                          </div>
                          <input
                            type={showSecret ? "text" : "password"}
                            placeholder={editingItem ? "•••••••••••••••••••• (leave blank to keep current)" : "AWS Secret Access Key"}
                            value={awsSecretAccessKey}
                            onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                            required={!editingItem}
                            className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                          />
                        </div>
 
                        <Input
                          label="AWS Region Name"
                          placeholder="e.g. us-east-1"
                          value={region}
                          onChange={(e) => setRegion(e.target.value)}
                          required
                        />
 
                        <Input
                          label="Bucket Name (Optional)"
                          placeholder="e.g. my-triage-attachments"
                          value={bucketName}
                          onChange={(e) => setBucketName(e.target.value)}
                          hint="Specify a default bucket name to test access and download from."
                        />
                      </>
                    )}
 
                    {error && (
                      <div className="rounded-md bg-rose-50 p-3 text-sm text-rose-800 border border-rose-200">
                        {error}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 gap-2">
                  <Button type="submit" disabled={saving}>
                    {saving ? "Saving..." : "Save Connection"}
                  </Button>
                  <Button type="button" variant="secondary" onClick={closeModal}>
                    Cancel
                  </Button>
                  {editingItem && (
                    <div className="flex-1 text-left mt-3 sm:mt-0">
                      <Button
                        type="button"
                        variant="secondary"
                        className="text-rose-600 border-rose-200 hover:bg-rose-50"
                        disabled={deleting}
                        onClick={handleDelete}
                      >
                        {deleting ? "Deleting..." : "Delete Service"}
                      </Button>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
